import moment from 'moment';
import { Employee, IEmployee, IEmployeeData } from '../employee';
import {
  ITimeOffType,
  ITimeOffTypeData,
  ITimeOffTypeFieldError,
  ITimeOffUnit,
  TimeOffType,
  TimeOffTypeFieldError,
  TimeOffUnit
} from '../timeoff';
import { CalendarHoliday } from '../timeoff-calendar';
import { IUser, IUserData, User } from '../user';
import {
  ITimeOffRequest,
  ITimeOffAlternate,
  ITimeOffAlternateData,
  ITimeOffApprover,
  ITimeOffApproverData,
  ITimeOffAttachment,
  ITimeOffAttachmentData,
  ITimeOffAttachmentFieldError,
  ITimeOffAttachmentFieldErrorData,
  ITimeOffRequestData,
  ITimeOffRequestFieldError,
  ITimeOffRequestFieldErrorData,
  ITimeOffStateInfoData,
  TimeOffStateEnum,
  ITimeOffRequestCommentFieldError,
  ITimeOffRequestCommentFieldErrorData
} from './timeoff-request.types';
import {
  AbstractAttachment,
  IAttachment,
  IAttachmentData
} from '../attachment';
import { AbstractMentionAttachmentComment } from '../comment/comment';
import {
  IMentionAttachmentComment,
  IMentionAttachmentCommentData
} from '../comment/comment.types';

export class TimeOffRequest extends CalendarHoliday implements ITimeOffRequest {
  readonly state?: TimeOffStateEnum;
  readonly timeoff_unit?: ITimeOffUnit;
  readonly type?: ITimeOffType | number;
  readonly employee?: IEmployee | number | null = null;
  readonly approvers?: ITimeOffApprover[] = [];
  readonly alternates?: ITimeOffAlternate[] | number[];
  readonly dates?: string[] | Date[];
  readonly rule_string?: string = '';
  readonly owner?: string = '';
  readonly start: string | Date = '';
  readonly end: string | Date = '';
  readonly entitlement_year: number;
  readonly notes?: string = '';
  readonly amount_requested?: number = 0;
  readonly type_requested?: number = 0;
  readonly type_requested_subtype: number = 0;

  readonly attachments: ITimeOffAttachment[] = [];

  readonly created_at?: string;
  readonly updated_at?: string;

  constructor(data: ITimeOffRequestData) {
    super(data);
    if (typeof data.state == 'number') {
      this.state = data.state;
    }

    if (data.timeoff_unit) {
      this.timeoff_unit = new TimeOffUnit(data.timeoff_unit);
    }

    if (data.type) {
      this.type = new TimeOffType(data.type as ITimeOffTypeData);
    }

    if (data.employee) {
      this.employee = new Employee(data.employee as IEmployeeData);
    }

    this.owner = data.owner ?? '';

    this.type_requested = data.type_requested;

    if (data.start) {
      this.start = data.start;
    }

    this.entitlement_year = data.entitlement_year;

    if (data.end) {
      this.end = data.end;
    }

    if (data.dates) {
      this.dates = data.dates;
    }

    if (data.rule_string) {
      this.rule_string = data.rule_string;
    }

    if (data.alternates) {
      this.alternates = (data.alternates as ITimeOffAlternateData[]).map(
        alternate => new TimeOffAlternate(alternate)
      );
    }

    this.amount_requested = data.amount_requested ?? 0;

    this.type_requested_subtype = data.type_requested_subtype ?? 0;

    if (data.approvers) {
      this.approvers = data.approvers.map(
        (approver: ITimeOffApproverData) => new TimeOffApprover(approver)
      );
    }

    if (data.attachments) {
      this.attachments = data.attachments.map(
        attachment => new TimeOffAttachment(attachment)
      );
    }

    this.notes = data.notes ?? '';

    if (data.created_at) {
      this.created_at = data.created_at;
    }

    if (data.updated_at) {
      this.updated_at = data.updated_at;
    }
  }

  getStateInfo(): ITimeOffStateInfoData {
    // Returns color based on state
    switch (this.state) {
      case TimeOffStateEnum.Draft:
        return { display_name: 'Draft', color: 'draft' };

      case TimeOffStateEnum.Submitted:
        return { display_name: 'Waiting for Approval', color: 'submitted' };

      case TimeOffStateEnum.Approved:
        return { display_name: 'Approved', color: 'approved' };

      case TimeOffStateEnum.Rejected:
        return { display_name: 'Rejected', color: 'reject' };

      case TimeOffStateEnum.Cancelled:
        return { display_name: 'Cancelled', color: 'cancel' };

      case TimeOffStateEnum.PaidHoliday:
        return { display_name: 'Holiday', color: 'blue' };

      case TimeOffStateEnum.UnpaidHoliday:
        return { display_name: 'Unpaid Holiday', color: 'orange' };

      default:
        return { display_name: '', color: 'white' };
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  // Manage attachments
  attachFile(file: File): void {
    this.attachments.push(new TimeOffAttachment({ file: file }));
  }

  detachFile(file: string | File): void {
    const index = this.attachments?.findIndex(
      attachment => attachment.file == file
    );

    if (index > -1) {
      this.attachments?.splice(index, 1);
    }
  }
}

export class TimeOffAlternate implements ITimeOffAlternate {
  id?: number;
  alternate?: IUserData | number;
  created_at?: string;
  updated_at?: string;

  constructor(data?: ITimeOffAlternateData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.alternate) {
        if (typeof data.alternate == 'object') {
          this.alternate = new User(data.alternate as IUserData);
        } else {
          this.alternate = data.alternate;
        }
      }

      if (data.created_at) {
        this.created_at = data.created_at;
      }
      if (data.updated_at) {
        this.updated_at = data.updated_at;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class TimeOffApprover implements ITimeOffApprover {
  id?: string;
  approver?: IUser | number;
  status: TimeOffStateEnum = TimeOffStateEnum.Draft;
  created_at?: string;
  updated_at?: string;

  constructor(data?: ITimeOffApproverData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.approver) {
        if (typeof data.approver == 'object') {
          this.approver = new User(data.approver as IUserData);
        } else {
          this.approver = data.approver;
        }
      }

      this.status = data.status ?? TimeOffStateEnum.Draft;

      if (data.created_at) {
        this.created_at = data.created_at;
      }
      if (data.updated_at) {
        this.updated_at = data.updated_at;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class TimeOffAttachment implements ITimeOffAttachment {
  readonly id?: number | undefined;
  readonly file: string | File;
  readonly filename: string = '';

  constructor(data: ITimeOffAttachmentData) {
    if (data.id) {
      this.id = data.id;
    }

    if (data.filename) {
      this.filename = data.filename;
    }

    this.file = data.file;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class TimeOffRequestCommentAttachment extends AbstractAttachment {
  constructor(data: IAttachmentData) {
    super(data);
  }

  toString(): string {
    return this.filename;
  }
}

export class TimeOffRequestComment extends AbstractMentionAttachmentComment
  implements IMentionAttachmentComment {
  readonly text: string = '';
  readonly attachments: IAttachment[] = [];

  constructor(data?: IMentionAttachmentCommentData) {
    super(data);

    if (data) {
      this.text = data.text ?? '';

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new TimeOffRequestCommentAttachment(attachment)
        );
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  toString() {
    return this.text;
  }
}

export class TimeOffRequestCommentFieldError
  implements ITimeOffRequestCommentFieldError {
  readonly text: string[] = [];

  constructor(data?: ITimeOffRequestCommentFieldErrorData) {
    if (data) {
      if (data.text) {
        this.text = data.text;
      }
    }
  }

  clearErrors(): void {
    this.text.splice(0, this.text.length);
  }

  static isTimeOffRequestCommentFieldError(
    data: ITimeOffRequestCommentFieldErrorData
  ): boolean {
    if (data.text && Array.isArray(data.text)) {
      return true;
    }
    return false;
  }
}

export class TimeOffAttachmentFieldError
  implements ITimeOffAttachmentFieldError {
  readonly file: string[] = [];

  constructor(data?: ITimeOffAttachmentFieldErrorData) {
    if (data) {
      if (data.file) {
        this.file = data.file;
      }
    }
  }

  clearErrors(): void {
    this.file.splice(0, this.file.length);
  }

  static isTimeOffAttachmentFieldError(
    data: ITimeOffAttachmentFieldErrorData
  ): boolean {
    if (data.file && Array.isArray(data.file)) {
      return true;
    }
    return false;
  }
}

export class TimeOffRequestFieldError implements ITimeOffRequestFieldError {
  readonly state: string[] = [];
  readonly type: ITimeOffTypeFieldError = new TimeOffTypeFieldError();
  readonly employee: string[] = [];
  readonly owner: string[] = [];
  readonly start: string[] = [];
  readonly end: string[] = [];
  readonly dates: string[] = [];
  readonly attachments: ITimeOffAttachmentFieldError[] = [];
  readonly type_requested: string[] = [];
  readonly amount_requested: string[] = [];
  readonly notes: string[] = [];

  constructor(data?: ITimeOffRequestFieldErrorData) {
    if (data) {
      if (data.state) {
        this.state = data.state;
      }
      if (data.employee) {
        this.employee = data.employee;
      }
      if (data.owner) {
        this.owner = data.owner;
      }
      if (data.start) {
        this.start = data.start;
      }
      if (data.end) {
        this.end = data.end;
      }
      if (data.dates) {
        this.dates = data.dates;
      }
      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new TimeOffAttachmentFieldError(attachment)
        );
      }
      if (data.amount_requested) {
        this.amount_requested = data.amount_requested;
      }
      if (data.type_requested) {
        this.type_requested = data.type_requested;
      }
      if (data.notes) {
        this.notes = data.notes;
      }
    }
  }

  clearErrors(): void {
    this.state.splice(0, this.state.length);
    this.employee.splice(0, this.employee.length);
    this.owner.splice(0, this.owner.length);
    this.start.splice(0, this.start.length);
    this.end.splice(0, this.end.length);
    this.notes.splice(0, this.notes.length);
    this.type_requested.splice(0, this.type_requested.length);
    this.attachments.splice(0, this.attachments.length);
    this.amount_requested.splice(0, this.amount_requested.length);
    this.type.clearErrors();
  }

  static isTimeOffFieldError(data: ITimeOffRequestFieldErrorData): boolean {
    if (data.state && Array.isArray(data.state)) {
      return true;
    } else if (data.employee && Array.isArray(data.employee)) {
      return true;
    } else if (data.owner && Array.isArray(data.owner)) {
      return true;
    } else if (data.start && Array.isArray(data.start)) {
      return true;
    } else if (data.end && Array.isArray(data.end)) {
      return true;
    } else if (data.notes && Array.isArray(data.notes)) {
      return true;
    } else if (data.attachments && Array.isArray(data.attachments)) {
      return true;
    } else if (data.amount_requested && Array.isArray(data.amount_requested)) {
      return true;
    } else if (
      data.type &&
      TimeOffTypeFieldError.isTimeOffTypeFieldError(data.type)
    ) {
      return true;
    }
    return false;
  }
}
